import { useState, useEffect, useCallback } from 'react';
import { Download, Filter, Calendar, ChevronDown, ArrowUpRight, ArrowDownLeft, Minus } from 'lucide-react';
import { formatINR } from '@/utils/currency';
import { api } from '@/utils/api';
import { DashboardSkeleton, ErrorBanner } from '@/components/ui/loading-states';

const INFLOW_TYPES = new Set(['EOI_PAYMENT', 'CAPITAL_PAYMENT', 'INVESTOR_PAYMENT', 'TRANCHE_PAYMENT', 'DIVIDEND', 'CASHBACK', 'REFUND']);
const OUTFLOW_API_TYPES = new Set(['BUILDER_PAYMENT', 'BROKER_COMMISSION', 'PLATFORM_FEE']);

const TYPE_LABELS = {
  EOI_PAYMENT: 'EOI Payment', CAPITAL_PAYMENT: 'Capital Payment', INVESTOR_PAYMENT: 'Investment',
  TRANCHE_PAYMENT: 'Tranche Payment', REFUND: 'Refund', CASHBACK: 'Cashback',
  DIVIDEND: 'Dividend', PLATFORM_FEE: 'Platform Fee', OTHER: 'Other',
};

const FILTER_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'eoi', label: 'EOI Payments' },
  { value: 'capital', label: 'Capital Payments' },
  { value: 'dividend', label: 'Dividends' },
  { value: 'cashback', label: 'Cashback' },
  { value: 'refund', label: 'Refunds' },
  { value: 'fee', label: 'Platform Fees' },
];

const FLOW_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'inflow', label: 'Inflows' },
  { value: 'outflow', label: 'Outflows' },
];

function transformTxns(apiTxns) {
  return apiTxns.map(t => {
    const type = t.type || 'OTHER';
    const isInflow = type === 'REFUND' || type === 'CASHBACK' || type === 'DIVIDEND';
    const isOutflow = type.includes('PAYMENT') || type.includes('FEE');
    return {
      id: t.transactionId || t.paymentId || t.ledgerEntryId || `T_${Math.random().toString(36).slice(2, 8)}`,
      date: (t.createdAt || t.date || '').slice(0, 10),
      type: TYPE_LABELS[type] || type.replace(/_/g, ' '),
      property: t.projectName || t.unitName || '-',
      unit: t.unitName || '-',
      amount: t.amount || 0,
      direction: isInflow ? 'inflow' : 'outflow',
      status: (t.status || 'completed').toLowerCase(),
      method: t.method || 'Bank Transfer',
      referenceId: t.reference || t.bankRef || '-',
      notes: t.notes || '',
    };
  });
}

export default function InvestorTransactionsTab({ user }) {
  const [typeFilter, setTypeFilter] = useState('all');
  const [flowFilter, setFlowFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [expandedRow, setExpandedRow] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiSummary, setApiSummary] = useState({});

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const userId = user?.id || user?.userId || '';
      const res = await api.get(`/investor/${userId}/transactions?limit=200`);
      setTransactions(transformTxns(res.data.transactions || []));
      setApiSummary(res.data.summary || {});
    } catch (e) {
      console.error('Failed to load transactions:', e);
      setError('Failed to load transactions.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const filtered = transactions.filter(tx => {
    if (typeFilter !== 'all') {
      const typeMap = { eoi: 'EOI Payment', capital: 'Capital Payment', dividend: 'Dividend', cashback: 'Cashback', refund: 'Refund', fee: 'Platform Fee' };
      if (tx.type !== typeMap[typeFilter]) return false;
    }
    if (flowFilter !== 'all' && tx.direction !== flowFilter) return false;
    return true;
  });

  const totalInflows = apiSummary.totalInvested || transactions.filter(t => t.direction === 'inflow' && t.status === 'completed').reduce((s, t) => s + t.amount, 0);
  const totalOutflows = apiSummary.totalRefunded || transactions.filter(t => t.direction === 'outflow' && t.status === 'completed').reduce((s, t) => s + t.amount, 0);
  const netPosition = (apiSummary.netInvested != null) ? apiSummary.netInvested : (totalInflows - totalOutflows);
  const pendingCount = transactions.filter(t => t.status === 'pending').length;

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  if (loading) return <div className="p-6 md:p-10"><DashboardSkeleton cards={4} rows={6} cols={5} /></div>;
  if (error) return <div className="p-6 md:p-10"><ErrorBanner message={error} onRetry={fetchTransactions} /></div>;

  const getDirectionIcon = (direction) => {
    if (direction === 'inflow') return <ArrowDownLeft className="w-4 h-4 text-[#4ADE80]" />;
    return <ArrowUpRight className="w-4 h-4 text-red-400" />;
  };

  const handleDownload = () => {
    alert('Downloading transaction statement... (Available in Phase 2)');
  };

  return (
    <div className="p-6 md:p-10" data-testid="investor-transactions">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-light text-white mb-2">Transactions</h1>
          <p className="text-sm text-gray-500">All payments, dividends, refunds and fees</p>
        </div>
        <button
          onClick={handleDownload}
          className="flex items-center gap-2 px-4 py-2 border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-colors text-sm"
          data-testid="download-statement-btn"
        >
          <Download className="w-4 h-4" />
          Download Statement
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/10 mb-8">
        <div className="bg-black px-4 md:px-6 py-5 md:py-6">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-2 md:mb-3">Total Inflows</p>
          <p className="font-mono text-xl md:text-2xl font-light text-[#4ADE80]" data-testid="total-inflows">+{formatINR(totalInflows)}</p>
        </div>
        <div className="bg-black px-4 md:px-6 py-5 md:py-6">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-2 md:mb-3">Total Outflows</p>
          <p className="font-mono text-xl md:text-2xl font-light text-red-400" data-testid="total-outflows">-{formatINR(totalOutflows)}</p>
        </div>
        <div className="bg-black px-4 md:px-6 py-5 md:py-6">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-2 md:mb-3">Net Position</p>
          <p className={`font-mono text-xl md:text-2xl font-light ${netPosition >= 0 ? 'text-[#4ADE80]' : 'text-red-400'}`} data-testid="net-position">
            {netPosition >= 0 ? '+' : '-'}{formatINR(Math.abs(netPosition))}
          </p>
        </div>
        <div className="bg-black px-4 md:px-6 py-5 md:py-6">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-2 md:mb-3">Pending</p>
          <p className="font-mono text-xl md:text-2xl font-light text-yellow-400" data-testid="pending-count">{pendingCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        {/* Flow filter tabs */}
        <div className="flex border border-white/10">
          {FLOW_FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setFlowFilter(f.value)}
              className={`px-4 py-2 text-xs font-medium transition-colors ${
                flowFilter === f.value ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-white'
              }`}
              data-testid={`flow-filter-${f.value}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-600" />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-white/5 border border-white/10 text-white px-3 py-2 text-sm"
            data-testid="type-filter"
          >
            {FILTER_TYPES.map(f => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>

        <span className="text-xs text-gray-600 ml-auto">{filtered.length} transactions</span>
      </div>

      {/* Transactions Table */}
      <div className="border border-white/10">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/10 bg-white/5">
          <div className="col-span-1"><p className="text-[10px] text-gray-600 uppercase tracking-widest"></p></div>
          <div className="col-span-2"><p className="text-[10px] text-gray-600 uppercase tracking-widest">Date</p></div>
          <div className="col-span-2"><p className="text-[10px] text-gray-600 uppercase tracking-widest">Type</p></div>
          <div className="col-span-3"><p className="text-[10px] text-gray-600 uppercase tracking-widest">Property</p></div>
          <div className="col-span-2 text-right"><p className="text-[10px] text-gray-600 uppercase tracking-widest">Amount</p></div>
          <div className="col-span-1"><p className="text-[10px] text-gray-600 uppercase tracking-widest">Status</p></div>
          <div className="col-span-1"></div>
        </div>

        {filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Minus className="w-8 h-8 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500">No transactions found</p>
            <p className="text-gray-600 text-xs mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          filtered.map((tx) => (
            <div key={tx.id}>
              <div
                className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-white/5 hover:bg-white/[0.03] transition-colors cursor-pointer"
                onClick={() => setExpandedRow(expandedRow === tx.id ? null : tx.id)}
                data-testid={`txn-row-${tx.id}`}
              >
                <div className="col-span-1 flex items-center">
                  {getDirectionIcon(tx.direction)}
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-white">{formatDate(tx.date)}</p>
                </div>
                <div className="col-span-2">
                  <p className={`text-sm ${
                    tx.direction === 'inflow' ? 'text-[#4ADE80]' : 'text-white'
                  }`}>{tx.type}</p>
                </div>
                <div className="col-span-3">
                  <p className="text-sm text-white">{tx.property}</p>
                  {tx.unit !== '-' && <p className="text-xs text-gray-600">{tx.unit}</p>}
                </div>
                <div className="col-span-2 text-right">
                  <p className={`font-mono text-sm ${
                    tx.direction === 'inflow' ? 'text-[#4ADE80]' : 'text-white'
                  }`}>
                    {tx.direction === 'inflow' ? '+' : '-'}{formatINR(tx.amount)}
                  </p>
                </div>
                <div className="col-span-1">
                  <span className={`text-xs px-2 py-1 ${
                    tx.status === 'completed' ? 'bg-[#4ADE80]/10 text-[#4ADE80]' : 'bg-yellow-500/10 text-yellow-400'
                  }`}>
                    {tx.status}
                  </span>
                </div>
                <div className="col-span-1 flex items-center justify-end">
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expandedRow === tx.id ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {/* Expanded Detail */}
              {expandedRow === tx.id && (
                <div className="px-6 py-4 bg-white/[0.02] border-b border-white/10">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Payment Method</p>
                      <p className="text-sm text-white">{tx.method}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Reference ID</p>
                      <p className="text-sm text-white font-mono">{tx.referenceId}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Transaction ID</p>
                      <p className="text-sm text-white font-mono">{tx.id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Notes</p>
                      <p className="text-sm text-gray-400">{tx.notes}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
